import { type MouseEvent, useRef, useState } from "react";

import { useI18n } from "@/i18n";

import { PageLoadingFallback } from "./PageLoadingFallback";

type AuthTab = "login" | "signup";

interface AuthTabsProps {
  activeTab: AuthTab;
  loginHref?: string | null;
  signupHref?: string | null;
  showSignupTab?: boolean;
}

const TAB_BASE_CLASS =
  "flex h-3 flex-1 items-center justify-center border-b-2 px-0.5 text-center text-[1rem] font-medium no-underline transition-colors sm:px-1 sm:text-sm";
const TAB_INACTIVE_CLASS =
  "border-transparent text-text-secondary hover:border-line-stroke-strong hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-line-stroke-strong";
const TAB_ACTIVE_CLASS = "border-text-primary text-text-primary";
const TAB_DISABLED_CLASS =
  "cursor-not-allowed border-transparent text-text-secondary opacity-40";

/**
 * Login/signup navigation styled as tabs.
 * Inactive items are links because Auth0 treats login and signup as separate screens.
 */
export function AuthTabs({
  activeTab,
  loginHref,
  signupHref,
  showSignupTab = true,
}: AuthTabsProps) {
  const { messages } = useI18n();
  const navigationStartedRef = useRef(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const handleNavigation = (event: MouseEvent<HTMLAnchorElement>) => {
    const isModifiedClick =
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey;

    if (isModifiedClick || event.defaultPrevented) {
      return;
    }

    event.preventDefault();

    if (navigationStartedRef.current) {
      return;
    }

    navigationStartedRef.current = true;
    setIsNavigating(true);

    const destination = event.currentTarget.href;

    // Allow React to commit and the browser to paint the spinner before the
    // full-page Auth0 navigation tears down the current screen.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.location.assign(destination);
      });
    });
  };

  return (
    <>
      {/* Login/signup navigation. Sticky under the 48px logo header on mobile. */}
      <nav
        className="sticky top-3 z-20 w-full max-w-[808px] bg-background-primary sm:static sm:z-auto"
        aria-label={messages.shared.tabs.ariaLabel}
      >
        <div className="flex border-b border-line-stroke-base">
          <AuthTabItem
            isActive={activeTab === "login"}
            href={loginHref}
            label={messages.shared.tabs.login}
            onNavigate={handleNavigation}
          />
          {showSignupTab && (
            <AuthTabItem
              isActive={activeTab === "signup"}
              href={signupHref}
              label={messages.shared.tabs.signup}
              onNavigate={handleNavigation}
            />
          )}
        </div>
      </nav>

      {/* Feedback shown while Auth0 navigates to the selected screen. */}
      {isNavigating ? (
        <div className="fixed inset-0 z-[60]">
          <PageLoadingFallback ariaLabel={messages.shared.loading.ariaLabel} />
        </div>
      ) : null}
    </>
  );
}

function AuthTabItem({
  isActive,
  href,
  label,
  onNavigate,
}: {
  isActive: boolean;
  href?: string | null;
  label: string;
  onNavigate: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  if (isActive) {
    return (
      <span
        className={`${TAB_BASE_CLASS} ${TAB_ACTIVE_CLASS}`}
        aria-current="page"
      >
        {label}
      </span>
    );
  }

  if (!href) {
    return (
      <span
        className={`${TAB_BASE_CLASS} ${TAB_DISABLED_CLASS}`}
        aria-disabled="true"
      >
        {label}
      </span>
    );
  }

  return (
    <a
      href={href}
      className={`${TAB_BASE_CLASS} ${TAB_INACTIVE_CLASS}`}
      onClick={onNavigate}
    >
      {label}
    </a>
  );
}
